import type { AgentAutogenSettingsRow } from '../types/agentAutogen';
import { supabase } from './supabaseStorage';

const settingsUrl = () => `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-autogenerate-settings`;

function parseEdgeErrorBody(text: string): { data: unknown; errorMessage?: string } {
  if (!text?.trim()) return { data: {} };
  try {
    const data = JSON.parse(text) as Record<string, unknown>;
    const errorMessage =
      typeof data.error === 'string'
        ? data.error
        : typeof data.message === 'string'
          ? data.message
          : undefined;
    return { data, errorMessage };
  } catch {
    return { data: { raw: text }, errorMessage: text.slice(0, 300) };
  }
}

async function edgeAuthJsonHeaders(): Promise<HeadersInit | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!session?.access_token || !anon) return null;
  return {
    Authorization: `Bearer ${session.access_token}`,
    apikey: anon,
    'Content-Type': 'application/json',
  };
}

async function post(body: Record<string, unknown>) {
  const headers = await edgeAuthJsonHeaders();
  if (!headers) {
    return { ok: false, status: 401, data: {}, errorMessage: 'Sign in required' };
  }
  const res = await fetch(settingsUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const text = await res.text();
  const { data, errorMessage } = parseEdgeErrorBody(text);
  return { ok: res.ok, status: res.status, data, errorMessage };
}

export async function fetchAgentAutogenSettings() {
  return post({
    action: 'get',
  });
}

export async function saveAgentAutogenSettings(params: {
  settings: AgentAutogenSettingsRow;
}) {
  return post({
    action: 'save',
    settings: params.settings,
  });
}

export async function previewAgentAutogenPrompt(params: {
  sampleAppDescription?: string;
}) {
  return post({
    action: 'preview',
    sample_app_description: params.sampleAppDescription ?? '',
  });
}
